import { DynamicForm } from "@shared/components/DynamicForm";
import { FormField, FormService } from "@shared/services/FormServices";
import { Badge } from "@shared/ui/badge";
import { Button } from "@shared/ui/button";
import { Checkbox } from "@shared/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@shared/ui/dialog";
import { Search } from "lucide-react";
import React, { useState } from "react";

export const SystemSearchModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  facilityName: string;
  onConnectSystems: (systemIds: string[]) => void;
}> = ({ isOpen, onClose, facilityName, onConnectSystems }) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedSystems, setSelectedSystems] = useState<string[]>([]);

  const allSystems = [
    {
      id: "SYS-201",
      name: "Advanced Filtration System",
      type: "Filtration",
      status: "Operational",
      location: "North District",
      facilityName: "Unassigned",
    },
    {
      id: "SYS-202",
      name: "UV Disinfection Unit",
      type: "Disinfection",
      status: "Operational",
      location: "Central Zone",
      facilityName: "Unassigned",
    },
    {
      id: "SYS-203",
      name: "Emergency Backup Pump",
      type: "Pumping",
      status: "Standby",
      location: "South District",
      facilityName: "Unassigned",
    },
    {
      id: "SYS-204",
      name: "Chemical Feed System",
      type: "Chemical Treatment",
      status: "Operational",
      location: "Treatment Plant Area",
      facilityName: "Unassigned",
    },
    {
      id: "SYS-205",
      name: "Storage Tank Assembly",
      type: "Storage",
      status: "Operational",
      location: "Reservoir Zone",
      facilityName: "Unassigned",
    },
  ];

  const searchFilterFields: FormField[] = [
    {
      name: "systemType",
      type: "select",
      label: "System Type",
      placeholder: "All Types",
      options: [
        { label: "All Types", value: "all" },
        { label: "Filtration", value: "Filtration" },
        { label: "Disinfection", value: "Disinfection" },
        { label: "Pumping", value: "Pumping" },
        { label: "Chemical Treatment", value: "Chemical Treatment" },
        { label: "Storage", value: "Storage" },
      ],
      defaultValue: "all",
      classes: {
        container: "w-full",
        select: "w-full h-10 px-3 border border-gray-300 rounded-lg",
      },
    },
    {
      name: "status",
      type: "select",
      label: "Status",
      placeholder: "All Statuses",
      options: [
        { label: "All Statuses", value: "all" },
        { label: "Operational", value: "Operational" },
        { label: "Standby", value: "Standby" },
        { label: "Maintenance", value: "Maintenance" },
      ],
      defaultValue: "all",
      classes: {
        container: "w-full",
        select: "w-full h-10 px-3 border border-gray-300 rounded-lg",
      },
    },
  ];

  const searchFilterForm = FormService.createForm({
    fields: searchFilterFields,
    defaultValues: {
      systemType: "all",
      status: "all",
    },
  });

  const handleSearchFilterSubmit = (data: any) => {};

  const handleSystemToggle = (systemId: string) => {
    setSelectedSystems((prev) =>
      prev.includes(systemId)
        ? prev.filter((id) => id !== systemId)
        : [...prev, systemId]
    );
  };

  const handleConnectSelected = () => {
    if (selectedSystems.length > 0) {
      onConnectSystems(selectedSystems);
      setSelectedSystems([]);
    }
  };

  const filteredSystems = allSystems.filter((system) => {
    const matchesSearch = system.name
      .toLowerCase()
      .includes(searchQuery.toLowerCase());
    return matchesSearch;
  });

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold text-gray-900">
            Connect Systems to {facilityName}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          <div className="space-y-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search systems by name, ID, or location..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 h-10 px-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <DynamicForm
              fields={searchFilterFields}
              form={searchFilterForm}
              onSubmit={handleSearchFilterSubmit}
              gridColumns={2}
              responsiveColumns={{ sm: 1, md: 2 }}
              config={{
                showProgress: false,
                layout: "vertical",
                showSubmitButton: false,
                showResetButton: false,
              }}
            />
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-medium text-gray-900">
                Available Systems ({filteredSystems.length})
              </h3>
              {selectedSystems.length > 0 && (
                <Badge
                  variant="secondary"
                  className="bg-blue-100 text-blue-800"
                >
                  {selectedSystems.length} selected
                </Badge>
              )}
            </div>

            <div className="max-h-96 overflow-y-auto space-y-2">
              {filteredSystems.map((system) => (
                <div
                  key={system.id}
                  className="flex items-center gap-3 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <Checkbox
                    id={system.id}
                    checked={selectedSystems.includes(system.id)}
                    onCheckedChange={() => handleSystemToggle(system.id)}
                  />
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-2">
                      <label
                        htmlFor={system.id}
                        className="font-medium text-gray-900 cursor-pointer"
                      >
                        {system.name}
                      </label>
                      <div className="flex gap-2">
                        <Badge variant="secondary">{system.type}</Badge>
                        <Badge variant="secondary">{system.status}</Badge>
                      </div>
                    </div>
                    <div className="flex items-center gap-4 text-sm text-gray-600">
                      <span>ID: {system.id}</span>
                      <span>Location: {system.location}</span>
                      <span>Currently: {system.facilityName}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="flex gap-3 pt-4 border-t">
            <Button
              onClick={handleConnectSelected}
              disabled={selectedSystems.length === 0}
              className="flex-1 bg-blue-600 hover:bg-blue-700"
            >
              Connect Selected ({selectedSystems.length})
            </Button>
            <Button variant="outline" onClick={onClose} className="px-8">
              Cancel
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
